/**
 * Portal-Monatskalender — Kacheln am Tag, Pane-Fit.
 * Tag-Klick öffnet die Tagesansicht (Liste), nicht nur den Create-Dialog.
 * Anlegen: Plus oder leerer Stundenslot → Popup (POPOVER_DISMISS_OUTSIDE).
 * Fristen aus `portal_fristen` nur anzeigen (Akt-Hub bleibt CRUD-Owner).
 * Klasse KALENDER_TILE_SEAT_OR_SKIP + KALENDER_DAY_OPEN_LIST + KALENDER_SEARCH_CLICK
 * + POPOVER_DISMISS_OUTSIDE.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight, ExternalLink, Loader2, Plus, Search, Trash2, X } from 'lucide-react';
import { PortalShortcutHint } from '../PortalShortcutHint';
import { usePortalShortcutAria } from '../portalShortcutContext';
import { listKanzleiSeatsForAktePicker } from '../portalAkten';
import type { KanzleiSeatForAktePicker } from '../portalAktenTypes';
import { placeSharePopover, portalShareSeatLabel } from '../portalDocumentsPrivacy';
import { usePopoverDismissOutside } from '../usePopoverDismissOutside';
import { listPortalFristenForAkte } from '../verfahrensmotor/portalFristen';
import type { PortalFristRecord } from '../verfahrensmotor/fristApprovalTypes';
import {
  CALENDAR_SEARCH_EMPTY_LABEL,
  PORTAL_KALENDER_DAY_CLASS,
  PORTAL_KALENDER_SEARCH_CLASS,
  WEEKDAY_LABELS_DE,
  addCalendarDays,
  addCalendarMonths,
  applyCalendarSearchDateJump,
  applyCalendarSearchHitClick,
  applyMonthDayClick,
  buildDayAgenda,
  buildDayHourRows,
  buildDayTiles,
  buildMonthGrid,
  eventAffectsMultipleSeats,
  eventsOnDate,
  formatHourLabel,
  formatLongDeDate,
  formatMonthHeading,
  fristLabelOnDate,
  fristTouchesIsoDate,
  isoDateLocal,
  DAY_POPOVER_EST_HEIGHT_PX,
  DAY_POPOVER_WIDTH_PX,
  parseFlexibleDate,
  parseFlexibleTime,
  parseIsoDateLocal,
  sanitizeKalenderSeatId,
  searchCalendarEvents,
  type CalendarSearchHit,
  type CalendarSurface,
  type PortalCalendarEvent,
} from '../calendar/portalCalendar';
import {
  deleteCalendarEventFromPb,
  loadCalendarEventsFromPb,
  saveCalendarEventToPb,
} from '../calendar/portalCalendarPersist';
import { noteSitzGlockePortalAction, sitzGlockeActorFromSeat } from '../sitzGlockeStore';

export interface KalenderAkteOption {
  id: string;
  title: string;
}

export interface KalenderViewProps {
  customerId: string;
  kanzleiId?: string;
  authToken?: string | null;
  akten?: KalenderAkteOption[];
  defaultAkteId?: string | null;
}

/** Schwarzer Grund, feine lila Kante — wie die Aktenliste. */
const KALENDER_GROUND = 'bg-[#07070a]';
const KALENDER_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
/** Knopf wie Neue Akte und Öffnen. */
const KALENDER_BTN =
  'inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-45';
/** Icon-Knopf wie die Leiste im Hauptreiter. */
const KALENDER_ICON =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55 disabled:cursor-not-allowed disabled:opacity-45';
const KALENDER_FIELD =
  'w-full rounded-md border border-violet-400/30 bg-[#07070a] text-sm text-slate-100 placeholder:text-slate-600 focus:border-violet-400 focus:outline-none [color-scheme:dark]';

/**
 * Dieselbe Würfel-Drehung wie Arko ON/off: rotateX, 500ms, Perspektive 280.
 * Nur wo der Termin-Dialog auf- und zuklappt.
 */
function KalenderCube({
  open,
  front,
  back,
}: {
  open: boolean;
  front: React.ReactNode;
  back: React.ReactNode;
}) {
  const face =
    'absolute inset-0 inline-flex h-full w-full items-center justify-center rounded-md [backface-visibility:hidden]';
  return (
    <span className="relative block h-7 w-7 shrink-0 [perspective:280px]" aria-hidden>
      <span
        className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d]"
        style={{ transform: open ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
      >
        <span className={`${face} border border-violet-400/40 bg-violet-600/30 text-violet-200`}>
          {front}
        </span>
        <span className={`${face} bg-violet-600 text-white`} style={{ transform: 'rotateX(180deg)' }}>
          {back}
        </span>
      </span>
    </span>
  );
}

function tileClass(kind: 'event' | 'frist', multiSeat?: boolean): string {
  if (kind === 'frist') {
    return 'bg-amber-500/15 text-amber-100 border-amber-400/25';
  }
  if (multiSeat) {
    return 'bg-violet-500/20 text-violet-100 border-violet-400/30';
  }
  return 'bg-[#07070a] text-violet-100 border-violet-400/40';
}

export const KalenderView: React.FC<KalenderViewProps> = ({
  customerId,
  kanzleiId = '',
  authToken,
  akten = [],
  defaultAkteId = null,
}) => {
  const neuShortcut = usePortalShortcutAria('neu');
  const today = useMemo(() => new Date(), []);
  const todayIso = isoDateLocal(today);
  const [cursor, setCursor] = useState(() => ({
    year: today.getFullYear(),
    monthIndex: today.getMonth(),
  }));
  const [surface, setSurface] = useState<CalendarSurface>('month');
  const [selectedIso, setSelectedIso] = useState(todayIso);
  const [events, setEvents] = useState<PortalCalendarEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [fristen, setFristen] = useState<PortalFristRecord[]>([]);
  const [fristenLoading, setFristenLoading] = useState(false);
  const [seats, setSeats] = useState<KanzleiSeatForAktePicker[]>([]);
  const [title, setTitle] = useState('');
  const [dateText, setDateText] = useState(todayIso);
  const [timeText, setTimeText] = useState('');
  const [akteId, setAkteId] = useState(defaultAkteId || '');
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [formHint, setFormHint] = useState('');
  const [loadError, setLoadError] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ left: 24, top: 96 });
  const [busy, setBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const surfaceRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const createTriggerRef = useRef<HTMLButtonElement | null>(null);

  const writeCtx = useMemo(
    () => ({
      customerId,
      kanzleiId: sanitizeKalenderSeatId(kanzleiId),
      visibleAkteIds: akten.map((a) => a.id),
      seatIds: seats.map((s) => s.id),
    }),
    [akten, customerId, kanzleiId, seats],
  );

  const closePopover = useCallback(() => {
    setPopoverOpen(false);
    setEditingId(null);
    setFormError('');
  }, []);

  usePopoverDismissOutside({
    open: popoverOpen,
    onClose: closePopover,
    rootRef: popoverRef,
    ignoreCloseRef: createTriggerRef,
    closeOnScroll: false,
  });

  const refreshEvents = useCallback(async () => {
    const kid = sanitizeKalenderSeatId(kanzleiId);
    if (!customerId || !kid) {
      setEvents([]);
      setLoadError('Kalender braucht eine Kanzlei — ohne Kanzlei keine Termine.');
      return;
    }
    setEventsLoading(true);
    try {
      const rows = await loadCalendarEventsFromPb({
        customerId,
        kanzleiId: kid,
        authToken,
      });
      setEvents(rows);
      setLoadError('');
    } catch (err) {
      setEvents([]);
      setLoadError(err instanceof Error ? err.message : 'Termine konnten nicht geladen werden.');
    } finally {
      setEventsLoading(false);
    }
  }, [authToken, customerId, kanzleiId]);

  useEffect(() => {
    void refreshEvents();
  }, [refreshEvents]);

  useEffect(() => {
    const token = String(authToken || '').trim();
    if (!customerId || !token) {
      setFristen([]);
      return;
    }
    let cancelled = false;
    setFristenLoading(true);
    void listPortalFristenForAkte(customerId, token).then((rows) => {
      if (!cancelled) {
        setFristen(rows);
        setFristenLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [authToken, customerId]);

  useEffect(() => {
    const token = String(authToken || '').trim();
    const kid = sanitizeKalenderSeatId(kanzleiId);
    if (!token || !kid) {
      setSeats([]);
      return;
    }
    let cancelled = false;
    void listKanzleiSeatsForAktePicker(token, kid).then((rows) => {
      if (!cancelled) setSeats(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [authToken, kanzleiId]);

  const cells = useMemo(
    () => buildMonthGrid(cursor.year, cursor.monthIndex, today),
    [cursor.monthIndex, cursor.year, today],
  );
  const heading = formatMonthHeading(cursor.year, cursor.monthIndex);
  const dayEvents = useMemo(() => eventsOnDate(events, selectedIso), [events, selectedIso]);
  const dayFristen = useMemo(
    () => fristen.filter((f) => fristTouchesIsoDate(f, selectedIso)),
    [fristen, selectedIso],
  );
  const dayAgenda = useMemo(
    () => buildDayAgenda(selectedIso, events, fristen),
    [events, fristen, selectedIso],
  );
  const hourRows = useMemo(() => buildDayHourRows(selectedIso, events), [events, selectedIso]);
  const untimedEvents = useMemo(() => dayEvents.filter((ev) => !ev.time), [dayEvents]);
  const otherSeats = useMemo(
    () => seats.filter((s) => s.id && s.id !== customerId),
    [customerId, seats],
  );
  const searchResult = useMemo(
    () =>
      searchCalendarEvents(searchQuery, events, {
        seatId: customerId,
        kanzleiId,
        visibleAkteIds: akten.map((a) => a.id),
      }),
    [akten, customerId, events, kanzleiId, searchQuery],
  );

  const syncCursorToIso = (iso: string) => {
    const d = parseIsoDateLocal(iso);
    if (!d) return;
    setCursor({ year: d.getFullYear(), monthIndex: d.getMonth() });
  };

  const goMonth = (delta: number) => {
    setCursor((prev) => addCalendarMonths(prev.year, prev.monthIndex, delta));
  };

  const goToday = () => {
    setCursor({ year: today.getFullYear(), monthIndex: today.getMonth() });
    setSelectedIso(todayIso);
    setDateText(todayIso);
    if (surface === 'day') {
      setPopoverOpen(false);
    }
  };

  const resetForm = (iso: string, ev?: PortalCalendarEvent | null) => {
    setSelectedIso(iso);
    setDateText(ev?.date || iso);
    setTimeText(ev?.time || '');
    setTitle(ev?.title || '');
    setAkteId(ev?.akteId || defaultAkteId || '');
    setAttendeeIds((ev?.attendeeIds || []).filter((id) => id && id !== customerId));
    setEditingId(ev?.id || null);
    setFormError('');
    setFormHint('');
  };

  const openDayView = (iso: string, inMonth: boolean) => {
    const opened = applyMonthDayClick(iso);
    if (!opened.selectedIso || !opened.dayViewVisible) return;
    resetForm(opened.selectedIso);
    if (!inMonth) syncCursorToIso(opened.selectedIso);
    setSurface(opened.surface);
    setPopoverOpen(opened.createPopoverOpen);
  };

  const backToMonth = () => {
    closePopover();
    setSurface('month');
    syncCursorToIso(selectedIso);
  };

  const goDay = (delta: number) => {
    const next = addCalendarDays(selectedIso, delta);
    if (!next) return;
    resetForm(next);
    syncCursorToIso(next);
    setPopoverOpen(false);
  };

  const placePopoverFromEl = (el: HTMLElement | null) => {
    const row = el?.closest('[data-kalender-hour-row]') as HTMLElement | null;
    const anchor = (row || el)?.getBoundingClientRect();
    if (anchor) {
      setPopoverPos(
        placeSharePopover(anchor, { width: window.innerWidth, height: window.innerHeight }, {
          width: DAY_POPOVER_WIDTH_PX,
          height: DAY_POPOVER_EST_HEIGHT_PX,
        }),
      );
    }
  };

  const openCreatePopover = (opts?: { time?: string; ev?: PortalCalendarEvent | null; anchor?: HTMLElement | null }) => {
    resetForm(selectedIso, opts?.ev || null);
    if (!opts?.ev && opts?.time) setTimeText(opts.time);
    placePopoverFromEl(opts?.anchor || createTriggerRef.current);
    setPopoverOpen(true);
  };

  const handleSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setFormError('');
      setFormHint('');
      const date = parseFlexibleDate(dateText);
      const time = parseFlexibleTime(timeText);
      if (!date) {
        setFormError('Datum ungültig.');
        return;
      }
      if (time === null) {
        setFormError('Uhrzeit ungültig — Tipp HH:MM oder Picker nutzen.');
        return;
      }
      const akte = akten.find((a) => a.id === akteId);
      const prevEvent = editingId ? events.find((row) => row.id === editingId) : null;
      const prevAttendees = new Set(
        (prevEvent?.attendeeIds || []).map((id) => sanitizeKalenderSeatId(id)).filter(Boolean),
      );
      const nextAttendees = new Set(
        attendeeIds.map((id) => sanitizeKalenderSeatId(id)).filter((id) => id && id !== customerId),
      );
      setBusy(true);
      try {
        const saved = await saveCalendarEventToPb({
          input: {
            id: editingId || undefined,
            title,
            date,
            time,
            akteId: akte?.id,
            akteTitle: akte?.title,
            attendeeIds,
          },
          ctx: writeCtx,
          authToken,
        });
        const eventId = sanitizeKalenderSeatId(saved?.id || editingId || '');
        const actor = sitzGlockeActorFromSeat(seats.find((s) => s.id === customerId) || null);
        if (eventId) {
          for (const targetId of nextAttendees) {
            if (prevAttendees.has(targetId)) continue;
            noteSitzGlockePortalAction({
              targetSeatId: targetId,
              actorSeatId: customerId,
              actorName: actor?.name,
              actorRole: actor?.role,
              source: 'termin',
              refId: eventId,
              kind: 'added',
            });
          }
          for (const targetId of prevAttendees) {
            if (nextAttendees.has(targetId)) continue;
            noteSitzGlockePortalAction({
              targetSeatId: targetId,
              actorSeatId: customerId,
              actorName: actor?.name,
              actorRole: actor?.role,
              source: 'termin',
              refId: eventId,
              kind: 'removed',
            });
          }
        }
        await refreshEvents();
        setTitle('');
        setTimeText('');
        setAttendeeIds([]);
        setEditingId(null);
        setDateText(date);
        setSelectedIso(date);
        syncCursorToIso(date);
        setPopoverOpen(false);
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
      } finally {
        setBusy(false);
      }
    },
    [
      akteId,
      akten,
      attendeeIds,
      authToken,
      customerId,
      dateText,
      editingId,
      events,
      refreshEvents,
      seats,
      timeText,
      title,
      writeCtx,
    ],
  );

  const handleDelete = async (ev: PortalCalendarEvent) => {
    setFormError('');
    setBusy(true);
    try {
      await deleteCalendarEventFromPb({ event: ev, ctx: writeCtx, authToken });
      const actor = sitzGlockeActorFromSeat(seats.find((s) => s.id === customerId) || null);
      const eventId = sanitizeKalenderSeatId(ev.id);
      for (const raw of ev.attendeeIds || []) {
        const targetId = sanitizeKalenderSeatId(raw);
        if (!targetId || targetId === customerId || !eventId) continue;
        noteSitzGlockePortalAction({
          targetSeatId: targetId,
          actorSeatId: customerId,
          actorName: actor?.name,
          actorRole: actor?.role,
          source: 'termin',
          refId: eventId,
          kind: 'removed',
        });
      }
      await refreshEvents();
      if (editingId === ev.id) {
        resetForm(selectedIso);
        setPopoverOpen(false);
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const toggleAttendee = (id: string) => {
    setAttendeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSearchDateJump = (iso: string) => {
    const opened = applyCalendarSearchDateJump(iso);
    if (!opened.selectedIso || !opened.dayViewVisible) return;
    resetForm(opened.selectedIso);
    syncCursorToIso(opened.selectedIso);
    setSurface(opened.surface);
    setPopoverOpen(false);
  };

  const handleSearchHitClick = (hit: CalendarSearchHit, anchor?: HTMLElement | null) => {
    const opened = applyCalendarSearchHitClick(hit);
    const ev = events.find((row) => row.id === hit.id) || null;
    resetForm(opened.selectedIso, ev);
    syncCursorToIso(opened.selectedIso);
    setSurface(opened.surface);
    if (ev) {
      placePopoverFromEl(anchor || createTriggerRef.current);
      setPopoverOpen(true);
    } else {
      setPopoverOpen(false);
    }
  };

  const pickerDate = parseFlexibleDate(dateText) || '';
  const pickerTime = parseFlexibleTime(timeText) || '';

  const renderSearchControls = () => (
    <div
      className="flex-1 min-w-[14rem] max-w-xl flex items-stretch gap-1.5"
      data-testid="portal-kalender-search"
      data-kalender-search-class={PORTAL_KALENDER_SEARCH_CLASS}
    >
      <label className="relative flex-1 min-w-0">
        <span className="sr-only">Kalender durchsuchen</span>
        <Search
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none"
          aria-hidden
        />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            if (searchResult.mode === 'date' && searchResult.dateIso) {
              e.preventDefault();
              handleSearchDateJump(searchResult.dateIso);
              return;
            }
            if (searchResult.mode === 'keyword' && searchResult.hits.length === 1) {
              e.preventDefault();
              handleSearchHitClick(searchResult.hits[0]);
            }
          }}
          placeholder="Titel, Stichwort oder Datum"
          className={`${KALENDER_FIELD} pl-8 pr-2 py-1.5`}
          autoComplete="off"
          data-testid="portal-kalender-search-input"
        />
      </label>
      <label className="block w-[9.75rem] flex-shrink-0">
        <span className="sr-only">Datum suchen</span>
        <input
          type="date"
          value={searchResult.mode === 'date' ? searchResult.dateIso : ''}
          onChange={(e) => {
            const iso = e.target.value;
            setSearchQuery(iso);
            if (iso) handleSearchDateJump(iso);
          }}
          className={`${KALENDER_FIELD} px-2 py-1.5`}
          data-testid="portal-kalender-search-date"
          aria-label="Datum suchen"
        />
      </label>
    </div>
  );

  const renderSearchResults = () => {
    if (searchResult.mode === 'idle') return null;
    return (
      <div
        className={`flex-shrink-0 mb-2 p-2 space-y-1 max-h-36 overflow-y-auto ${KALENDER_EDGE}`}
        data-testid="portal-kalender-search-results"
        data-kalender-search-mode={searchResult.mode}
      >
        {searchResult.emptyVisible ? (
          <p
            className="text-xs text-slate-400 px-1"
            data-testid="portal-kalender-search-empty"
            role="status"
          >
            {CALENDAR_SEARCH_EMPTY_LABEL}
          </p>
        ) : null}
        {searchResult.hits.map((hit) => (
          <button
            key={hit.id}
            type="button"
            data-testid="portal-kalender-search-hit"
            onClick={(e) => handleSearchHitClick(hit, e.currentTarget)}
            className={`w-full text-left rounded-md border px-2 py-1 ${tileClass('event', hit.multiSeat)}`}
          >
            <p className="text-sm text-slate-100 truncate">{hit.title}</p>
            <p className="text-[11px] text-slate-500 truncate">
              {hit.time ? `${hit.time} · ` : ''}
              {formatLongDeDate(hit.date)}
              {hit.akteTitle ? ` · ${hit.akteTitle}` : ''}
            </p>
          </button>
        ))}
        {searchResult.jumpToDay && searchResult.dateIso ? (
          <button
            type="button"
            data-testid="portal-kalender-search-jump-day"
            onClick={() => handleSearchDateJump(searchResult.dateIso)}
            className={KALENDER_ICON}
            aria-label="Tag öffnen"
            title="Tag öffnen"
          >
            <ExternalLink className="w-3.5 h-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className={`w-full min-w-0 max-w-none flex flex-1 flex-col min-h-0 h-full overflow-hidden gap-2 px-3 py-3 ${KALENDER_GROUND}`}
      data-testid="portal-kalender-pane"
      data-portal-kalender="1"
      data-kalender-surface={surface}
      data-kalender-search-class={PORTAL_KALENDER_SEARCH_CLASS}
    >
      <header className={`flex-shrink-0 px-3.5 py-3 ${KALENDER_EDGE}`}>
        <h1 className="m-0 text-sm font-semibold text-white">Kalender</h1>
        <p className="m-0 mt-0.5 text-[11px] text-slate-500">
          Monat als Übersicht. Suche nach Titel oder Datum. Tag öffnet den Tag — Termine über Plus oder freien Slot.
        </p>
      </header>

      {surface === 'month' ? (
        <section
          ref={surfaceRef}
          className={`${KALENDER_EDGE} flex-1 min-h-0 flex flex-col p-3 sm:p-4`}
          aria-label="Monatsansicht"
          data-testid="portal-kalender-month-view"
        >
          <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
            <h2 className="text-white text-base font-semibold tracking-tight min-w-0 sm:min-w-[8rem]">
              {heading}
            </h2>
            {renderSearchControls()}
            <button
              type="button"
              onClick={goToday}
              className={KALENDER_BTN}
            >
              Heute
            </button>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => goMonth(-1)}
                className={KALENDER_ICON}
                aria-label="Vorheriger Monat"
                title="Vorheriger Monat"
              >
                <ChevronLeft className="w-4 h-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => goMonth(1)}
                className={KALENDER_ICON}
                aria-label="Nächster Monat"
                title="Nächster Monat"
              >
                <ChevronRight className="w-4 h-4" aria-hidden />
              </button>
            </div>
          </div>
          {renderSearchResults()}

          <div className="grid grid-cols-7 gap-0.5 flex-shrink-0 mb-1">
            {WEEKDAY_LABELS_DE.map((label) => (
              <div key={label} className="text-[11px] font-medium text-slate-500 text-center pb-1">
                {label}
              </div>
            ))}
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-7 grid-rows-6 gap-0.5">
            {cells.map((cell) => {
              const packed = buildDayTiles(cell.iso, events, fristen);
              const selected = cell.iso === selectedIso;
              return (
                <button
                  key={cell.iso}
                  type="button"
                  onClick={() => openDayView(cell.iso, cell.inMonth)}
                  aria-pressed={selected}
                  aria-label={`${formatLongDeDate(cell.iso)}${
                    packed.tiles.length
                      ? `, ${packed.tiles.map((t) => t.title).join(', ')}`
                      : ''
                  }`}
                  data-testid={`kalender-day-${cell.iso}`}
                  className={`min-h-0 h-full rounded-lg border text-left px-1 py-1 flex flex-col overflow-hidden transition-colors ${
                    selected
                      ? 'border-violet-400/70 bg-violet-600/15'
                      : cell.isToday
                        ? 'border-violet-400/50 bg-[#07070a]'
                        : 'border-violet-400/20 bg-[#07070a] hover:border-violet-400/45'
                  } ${cell.inMonth ? '' : 'opacity-40'}`}
                >
                  <span
                    className={`inline-flex items-center justify-center min-w-[1.35rem] h-5 text-xs font-medium rounded-full flex-shrink-0 ${
                      cell.isToday
                        ? 'bg-violet-600 text-white px-1.5'
                        : cell.isWeekend
                          ? 'text-slate-500'
                          : 'text-slate-200'
                    }`}
                  >
                    {cell.day}
                  </span>
                  <span className="mt-0.5 flex flex-col gap-0.5 min-h-0 overflow-hidden">
                    {packed.tiles.map((tile) => (
                      <span
                        key={tile.id}
                        data-kalender-tile={tile.kind === 'frist' ? 'frist' : tile.multiSeat ? 'multi' : 'single'}
                        data-testid={tile.kind === 'event' ? 'kalender-event-tile' : 'kalender-frist-tile'}
                        className={`block truncate text-[10px] leading-tight px-1 py-0.5 rounded border ${tileClass(
                          tile.kind,
                          tile.multiSeat,
                        )}`}
                      >
                        {tile.title}
                      </span>
                    ))}
                    {packed.hidden > 0 ? (
                      <span className="text-[10px] text-slate-500 px-1">+{packed.hidden}</span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>

          <p className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500 flex-shrink-0">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm border border-violet-400/50 bg-[#07070a]" aria-hidden /> Ein Sitz
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-violet-400/80" aria-hidden /> Mehrere Sitze
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-amber-400/80" aria-hidden /> Frist
            </span>
            {eventsLoading ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" aria-hidden /> Termine…
              </span>
            ) : null}
          </p>
          {loadError ? (
            <p className="mt-1 text-[11px] text-amber-300/90 flex-shrink-0" role="status">
              {loadError}
            </p>
          ) : null}
        </section>
      ) : (
        <section
          ref={surfaceRef}
          className={`${KALENDER_EDGE} flex-1 min-h-0 flex flex-col p-3 sm:p-4`}
          aria-label={`Tagesansicht ${formatLongDeDate(selectedIso)}`}
          data-testid="portal-kalender-day-view"
          data-kalender-class={PORTAL_KALENDER_DAY_CLASS}
          data-day-iso={selectedIso}
        >
          <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
            <button
              type="button"
              onClick={backToMonth}
              className={KALENDER_ICON}
              data-testid="portal-kalender-back-month"
              aria-label="Monat"
              title="Monat"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden />
            </button>
            <h2 className="text-white text-base font-semibold tracking-tight min-w-0 sm:min-w-[8rem] capitalize">
              {formatLongDeDate(selectedIso)}
            </h2>
            {renderSearchControls()}
            <button
              type="button"
              onClick={goToday}
              className={KALENDER_BTN}
            >
              Heute
            </button>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => goDay(-1)}
                className={KALENDER_ICON}
                aria-label="Vorheriger Tag"
                title="Vorheriger Tag"
              >
                <ChevronLeft className="w-4 h-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => goDay(1)}
                className={KALENDER_ICON}
                aria-label="Nächster Tag"
                title="Nächster Tag"
              >
                <ChevronRight className="w-4 h-4" aria-hidden />
              </button>
            </div>
            <button
              ref={createTriggerRef}
              type="button"
              onClick={() => {
                if (popoverOpen && !editingId) {
                  closePopover();
                  return;
                }
                openCreatePopover({ anchor: createTriggerRef.current });
              }}
              className="relative h-7 w-7 shrink-0 p-0"
              data-testid="portal-kalender-day-create"
              data-portal-shortcut="neu"
              aria-keyshortcuts={neuShortcut}
              aria-label="Termin anlegen"
              aria-expanded={popoverOpen && !editingId}
              title={popoverOpen && !editingId ? 'Schließen' : neuShortcut}
            >
              <KalenderCube
                open={popoverOpen && !editingId}
                front={<Plus className="w-3.5 h-3.5" aria-hidden />}
                back={<X className="w-3.5 h-3.5" aria-hidden />}
              />
            </button>
            <PortalShortcutHint id="neu" className="border-violet-300/40 text-violet-200/80" />
          </div>
          {renderSearchResults()}

          <div
            className="flex-1 min-h-0 flex flex-col overflow-hidden"
            data-testid="portal-kalender-day-list"
          >
            {fristenLoading || eventsLoading ? (
              <p className="flex items-center gap-2 text-xs text-slate-500 flex-shrink-0 mb-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                Einträge werden geladen…
              </p>
            ) : null}

            <div className="flex-shrink-0 max-h-[28%] overflow-y-auto space-y-1.5 pb-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Ganztägig / ohne Zeit</p>
              {dayFristen.length === 0 && untimedEvents.length === 0 ? (
                <p className="text-xs text-slate-500 px-1">Keine ganztägigen Einträge.</p>
              ) : null}
              {dayAgenda.length === 0 ? (
                <p className="text-[11px] text-slate-500 px-1" data-testid="portal-kalender-day-empty">
                  Keine Einträge — Plus oder freien Slot nutzen.
                </p>
              ) : null}
              {dayFristen.map((frist) => (
                <div
                  key={frist.id}
                  data-testid="portal-kalender-day-entry"
                  data-kalender-day-kind="frist"
                  className="rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-2.5 py-1.5"
                >
                  <p className="text-[10px] uppercase tracking-wider text-amber-400/90 font-medium">
                    Frist
                  </p>
                  <p className="text-sm text-slate-100 mt-0.5">{fristLabelOnDate(frist, selectedIso)}</p>
                </div>
              ))}
              {untimedEvents.map((ev) => {
                const multi = eventAffectsMultipleSeats(ev);
                return (
                  <button
                    key={ev.id}
                    type="button"
                    data-testid="portal-kalender-day-entry"
                    data-kalender-day-kind="event"
                    onClick={(e) => openCreatePopover({ ev, anchor: e.currentTarget })}
                    className={`w-full text-left rounded-lg border px-2.5 py-1.5 ${tileClass('event', multi)}`}
                  >
                    <p className="text-[10px] uppercase tracking-wider font-medium text-violet-200">
                      Termin{multi ? ' · mehrere Sitze' : ''}
                    </p>
                    <p className="text-sm text-slate-100 mt-0.5">{ev.title}</p>
                    {ev.akteTitle ? (
                      <p className="text-[11px] text-slate-500 mt-0.5 truncate">Akte: {ev.akteTitle}</p>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div
              className="flex-1 min-h-0 flex flex-col overflow-hidden border-t border-violet-400/20"
              data-testid="portal-kalender-day-hours"
              data-kalender-hours-fit="1"
            >
              {hourRows.map((row) => (
                <div
                  key={row.hour}
                  data-kalender-hour-row=""
                  className="grid grid-cols-[3.25rem_minmax(0,1fr)] gap-2 flex-1 min-h-0 border-t border-violet-400/15"
                  data-testid={`kalender-hour-${row.hour}`}
                >
                  <button
                    type="button"
                    onClick={(e) =>
                      openCreatePopover({ time: formatHourLabel(row.hour), anchor: e.currentTarget })
                    }
                    className="text-[11px] text-slate-500 text-left self-start pt-1 hover:text-slate-300"
                    aria-label={`Termin um ${row.label} anlegen`}
                  >
                    {row.label}
                  </button>
                  <div className="min-w-0 min-h-0 h-full py-0.5 flex flex-col gap-0.5 overflow-y-auto">
                    {row.events.length === 0 ? (
                      <button
                        type="button"
                        onClick={(e) =>
                          openCreatePopover({ time: formatHourLabel(row.hour), anchor: e.currentTarget })
                        }
                        className="flex-1 min-h-0 w-full rounded-md hover:bg-violet-600/10 text-left px-1"
                        aria-label={`Leerer Slot ${row.label}`}
                        data-testid={`kalender-hour-slot-${row.hour}`}
                      />
                    ) : (
                      row.events.map((ev) => {
                        const multi = eventAffectsMultipleSeats(ev);
                        return (
                          <button
                            key={ev.id}
                            type="button"
                            data-testid="portal-kalender-day-entry"
                            data-kalender-day-kind="event"
                            onClick={(e) => openCreatePopover({ ev, anchor: e.currentTarget })}
                            className={`w-full text-left rounded-md border px-2 py-0.5 min-h-0 ${tileClass('event', multi)}`}
                          >
                            <p className="text-[10px] text-slate-400">
                              {ev.time}
                              {multi ? ' · mehrere Sitze' : ''}
                            </p>
                            <p className="text-sm text-slate-100 truncate">{ev.title}</p>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>

            {loadError ? (
              <p className="text-[11px] text-amber-300/90 flex-shrink-0 mt-1" role="status">
                {loadError}
              </p>
            ) : null}
          </div>
        </section>
      )}

      {popoverOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              aria-label={editingId ? 'Termin ändern' : 'Termin anlegen'}
              data-testid="portal-kalender-day-popover"
              data-kalender-popover-fit="viewport-scroll"
              data-popover-dismiss="POPOVER_DISMISS_OUTSIDE"
              className="fixed z-[85] flex flex-col w-[min(100vw-1.5rem,25rem)] overflow-hidden rounded-xl border border-violet-400/30 bg-[#07070a] shadow-2xl"
              style={{
                left: popoverPos.left,
                top: popoverPos.top,
                maxHeight: `min(calc(100dvh - 1rem), calc(100dvh - ${popoverPos.top}px - 0.75rem))`,
              }}
            >
              <div className="flex items-start gap-2 flex-shrink-0 p-3 pb-2">
                <CalendarDays className="w-4 h-4 text-violet-300 mt-0.5 flex-shrink-0" aria-hidden />
                <div className="min-w-0 flex-1">
                  <h2 className="text-white text-sm font-semibold leading-snug">
                    {editingId ? 'Termin ändern' : 'Termin anlegen'}
                  </h2>
                  <p className="text-slate-500 text-xs mt-0.5 capitalize">
                    {formatLongDeDate(selectedIso)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closePopover}
                  className={KALENDER_ICON}
                  aria-label="Schließen"
                >
                  <X className="w-4 h-4" aria-hidden />
                </button>
              </div>

              <form onSubmit={(e) => void handleSave(e)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="min-h-0 flex-1 overflow-y-auto space-y-2.5 px-3 pb-2">
                <p className="text-xs font-medium text-slate-300 flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" aria-hidden />
                  {editingId ? 'Änderungen speichern' : 'Neuer Termin'}
                </p>
                <label className="block">
                  <span className="sr-only">Titel</span>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    maxLength={160}
                    placeholder="Überschrift"
                    className={`${KALENDER_FIELD} px-3 py-2`}
                  />
                </label>
                <div className="grid grid-cols-2 gap-2" data-testid="portal-kalender-datetime">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Datum</span>
                    <input
                      type="date"
                      value={pickerDate}
                      onChange={(e) => setDateText(e.target.value)}
                      required
                      className={`${KALENDER_FIELD} px-3 py-2 mt-0.5`}
                      aria-label="Datum"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Zeit</span>
                    <input
                      type="text"
                      value={timeText}
                      onChange={(e) => setTimeText(e.target.value)}
                      placeholder="HH:MM"
                      className={`${KALENDER_FIELD} px-3 py-2 mt-0.5`}
                    />
                    <input
                      type="time"
                      value={pickerTime}
                      onChange={(e) => setTimeText(e.target.value)}
                      className={`${KALENDER_FIELD} px-3 py-1.5 mt-1`}
                      aria-label="Uhrzeit wählen"
                    />
                  </label>
                </div>
                {akten.length > 0 ? (
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Akte (optional)</span>
                    <select
                      value={akteId}
                      onChange={(e) => setAkteId(e.target.value)}
                      className={`${KALENDER_FIELD} px-3 py-2 mt-0.5`}
                    >
                      <option value="">Keine Akte</option>
                      {akten.map((akte) => (
                        <option key={akte.id} value={akte.id}>
                          {akte.title}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {otherSeats.length > 0 ? (
                  <fieldset className="space-y-1">
                    <legend className="text-[10px] uppercase tracking-wider text-slate-500">
                      Sitze benachrichtigen
                    </legend>
                    <div className="max-h-28 overflow-y-auto space-y-1">
                      {otherSeats.map((seat) => (
                        <label key={seat.id} className="flex items-center gap-2 text-xs text-slate-300">
                          <input
                            type="checkbox"
                            checked={attendeeIds.includes(seat.id)}
                            onChange={() => toggleAttendee(seat.id)}
                          />
                          <span className="truncate">{portalShareSeatLabel(seat)}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ) : null}
                {editingId ? (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-slate-500 truncate">
                      {dayEvents.find((ev) => ev.id === editingId)?.title || 'Termin'}
                    </p>
                    {dayEvents.find((ev) => ev.id === editingId)?.customerId === customerId ? (
                      <button
                        type="button"
                        onClick={() => {
                          const ev = dayEvents.find((row) => row.id === editingId);
                          if (ev) void handleDelete(ev);
                        }}
                        className="p-1 rounded-md text-slate-500 hover:text-red-300 hover:bg-red-500/10"
                        aria-label="Termin löschen"
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden />
                      </button>
                    ) : null}
                  </div>
                ) : null}
                </div>
                {formError ? (
                  <p className="text-xs text-red-400 px-3 pt-2" role="alert">
                    {formError}
                  </p>
                ) : null}
                {formHint ? <p className="text-xs text-slate-500 px-3 pt-2">{formHint}</p> : null}
                <div className="flex gap-2 flex-shrink-0 p-3 pt-2 border-t border-white/[0.06]">
                  <button
                    type="submit"
                    disabled={busy}
                    className={`${KALENDER_BTN} flex-1 justify-center gap-2`}
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> : null}
                    Speichern
                  </button>
                  {editingId ? (
                    <button
                      type="button"
                      onClick={() => resetForm(selectedIso)}
                      className={KALENDER_BTN}
                    >
                      Neu
                    </button>
                  ) : null}
                </div>
              </form>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default KalenderView;
